import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, MessageCircle, CheckCircle2, AlertCircle, Send, Loader2 } from 'lucide-react';
import { FacebookIcon, InstagramIcon, TwitterIcon, YoutubeIcon } from '../components/SocialIcons';
import { StaggerGroup, StaggerItem } from '../components/Stagger';
import MagneticButton from '../components/MagneticButton';
import Reveal from '../components/Reveal';
import { useIsMobile } from '../hooks/useIsMobile';

// Real Reach Multispeciality Clinic location — click-through target and the visual embed.
const tambaramSearchUrl = 'https://maps.app.goo.gl/AbouRRdtikLvNMT2A';
const tambaramEmbedSrc = 'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d486.0050515577675!2d80.1496906!3d12.9692659!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a525f8fa9aee0c3%3A0xebb10fbcbd44ca7d!2sReach%20Multispeciality%20Clinic!5e0!3m2!1sen!2sin!4v1784496125331!5m2!1sen!2sin';

// The PHP endpoint on the GoDaddy hosting account. A relative path works once
// the built React app is deployed under the same domain as the PHP files
// (e.g. both under public_html) — no CORS issue there since it's same-origin.
const CONTACT_API_URL = 'https://reachgroups.co.in/api/contact-submit.php';

const isValidIndianPhone = (raw: string): boolean => {
  const digits = raw.replace(/\D/g, '');
  const last10 = digits.slice(-10);
  return /^[6-9]\d{9}$/.test(last10);
};

const isValidEmail = (raw: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim());

const Contact: React.FC = () => {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({ name: '', phone: '', email: '', vertical: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setStatus('error');
      setErrorMsg('Please enter your name.');
      return;
    }
    if (!isValidIndianPhone(form.phone)) {
      setStatus('error');
      setErrorMsg('Please enter a valid 10-digit mobile number.');
      return;
    }
    if (form.email.trim() !== '' && !isValidEmail(form.email)) {
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      const res = await fetch(CONTACT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Something went wrong. Please try again.');
      }

      setStatus('success');
      setTimeout(() => {
        setStatus('idle');
        setForm({ name: '', phone: '', email: '', vertical: '', message: '' });
      }, 4000);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  const waUrl = `https://wa.me/919789890769?text=Hello%20Reach%20Groups%2C%20I%20would%20like%20to%20enquire%20about%20your%20services.`;

  const infoItems = [
    { Icon: MapPin, title: 'Address', info: 'Pillayar Koil Street, C. Pallavaram, Chennai – 600043', link: null },
    { Icon: Phone, title: 'Phone', info: '+91 97898 90769', link: 'tel:+919789890769' },
    { Icon: Mail, title: 'Email', info: 'reachgroups26@gmail.com', link: 'mailto:reachgroups26@gmail.com' },
    { Icon: Clock, title: 'Business Hours', info: '09:00 AM – 09:00 PM (Monday – Sunday)', link: null },
  ];

  return (
    <>
      <section className="matte-dark grain" style={{ paddingTop: 140, paddingBottom: 130, position: 'relative', overflow: 'hidden' }}>
        <div className="blob float" style={{ top: -100, right: -80, width: 420, height: 420, background: 'radial-gradient(circle, rgba(214,40,40,0.14) 0%, transparent 70%)' }} />
        <div className="blob float" style={{ bottom: -120, left: -80, width: 360, height: 360, background: 'radial-gradient(circle, rgba(0,91,102,0.22) 0%, transparent 70%)', animationDelay: '1s' }} />

        {/* Google Maps, desaturated + tinted + masked so it reads as background
            texture on the right of the hero rather than a bright embedded map. */}
        <div aria-hidden style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: '52%', zIndex: 0,
          WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, #000 30%, #000 100%)',
          maskImage: 'linear-gradient(90deg, transparent 0%, #000 30%, #000 100%)',
        }}>
          <iframe
            title="Reach Groups location"
            src="https://maps.google.com/maps?q=C.+Pallavaram+Chennai&output=embed"
            width="100%" height="100%" style={{ border: 'none', display: 'block', filter: 'grayscale(1) invert(0.92) contrast(0.9)', pointerEvents: 'none' }}
            loading="lazy" />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(100deg, #061c1fee 0%, #0a2a2eaa 40%, #0b3e4488 100%)' }} />
          <motion.div
            animate={isMobile ? undefined : { y: [0, -8, 0] }} transition={isMobile ? undefined : { duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
            style={{ position: 'absolute', top: isMobile ? '88%' : '38%', left: isMobile ? '82%' : '48%', transform: 'translate(-50%,-50%)' }}
          >
            <div style={{ position: 'relative', width: isMobile ? 38 : 54, height: isMobile ? 38 : 54 }}>
              {!isMobile && (
                <motion.div
                  style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(214,40,40,0.3)' }}
                  animate={{ scale: [1, 1.8], opacity: [0.5, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
                />
              )}
              <div style={{ position: 'relative', width: isMobile ? 38 : 54, height: isMobile ? 38 : 54, borderRadius: '50%', background: '#D62828', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 26px rgba(214,40,40,0.5)' }}>
                <MapPin size={isMobile ? 18 : 26} color="#fff" fill="#fff" />
              </div>
            </div>
          </motion.div>
        </div>

        <div className="container" style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <span className="chip" style={{ background: 'rgba(255,255,255,0.1)', color: '#fff' }}>We're Here to Help</span>
          <h1 style={{ fontFamily: 'Poppins, sans-serif', fontSize: 'clamp(30px,4vw,46px)', fontWeight: 800, color: '#fff', margin: '14px 0 12px' }}>Contact Reach Groups</h1>
          <p style={{ color: 'rgba(255,255,255,0.72)', fontSize: 16, maxWidth: 560, margin: '0 auto', lineHeight: 1.7 }}>
            Reach us via phone, WhatsApp or the contact form below. Our team is available 9 AM to 9 PM, all days.
          </p>
        </div>
      </section>

      {/* GET IN TOUCH — a single two-tone card: form on light, details on a
          dark teal panel with a dotted texture, echoing the reference layout
          but in the site's own brand colours instead of the inspo's blue. */}
      <section style={{ padding: '64px 0 90px', background: '#F7F9FA', position: 'relative', marginTop: -46 }}>
        <div className="container">
          <Reveal>
            <div className="contact-card" style={{
              display: 'grid', gridTemplateColumns: '1.05fr 0.95fr', borderRadius: 26, overflow: 'hidden',
              boxShadow: '0 26px 64px rgba(0,91,102,0.2)', background: '#fff',
            }}>
              {/* FORM — light half, boxed off from the page with its own border.
                  On success, the entire heading+fields area is replaced by a
                  centered confirmation view instead of a small banner sitting
                  above still-visible fields. */}
              <div style={{ padding: '44px 40px', background: '#fff', border: '1px solid rgba(0,91,102,0.08)', borderRight: 'none', position: 'relative', minHeight: 560 }}>
                {status === 'success' ? (
                  <div style={{
                    position: 'absolute', inset: 0, background: '#fff', zIndex: 5,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '0 40px',
                  }}>
                    <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
                      <CheckCircle2 size={64} color="#16a34a" style={{ marginBottom: 20 }} />
                    </motion.div>
                    <h2 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: 26, color: '#1a1a1a', marginBottom: 10 }}>Thank you!</h2>
                    <p style={{ fontSize: 16, color: '#6B7280', maxWidth: 320, lineHeight: 1.7 }}>We'll get back to you within 2 hours.</p>
                  </div>
                ) : (
                  <>
                    <h2 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 22, color: '#1a1a1a', marginBottom: 4 }}>Send Us a Message</h2>
                    <p style={{ fontSize: 13.5, color: '#6B7280', marginBottom: 22 }}>Fill in the form and our team will get back to you shortly.</p>

                    {status === 'error' && (
                      <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: 14, marginBottom: 18, color: '#991b1b', fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <AlertCircle size={18} /> {errorMsg}
                      </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
                        <div>
                          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Full Name *</label>
                          <input type="text" placeholder="Your full name" value={form.name}
                            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                            style={{ width: '100%', padding: '12px 15px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none', transition: 'border 0.2s', background: '#fff', boxSizing: 'border-box' }}
                            onFocus={e => e.target.style.borderColor = '#005B66'}
                            onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                        </div>
                        <div>
                          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Phone Number *</label>
                          <input type="tel" inputMode="numeric" maxLength={13} placeholder="+91 9XXXXXXXXX" value={form.phone}
                            onChange={e => setForm(p => ({ ...p, phone: e.target.value.replace(/[a-zA-Z]/g, '') }))}
                            style={{ width: '100%', padding: '12px 15px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none', transition: 'border 0.2s', background: '#fff', boxSizing: 'border-box' }}
                            onFocus={e => e.target.style.borderColor = '#005B66'}
                            onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                        </div>
                      </div>

                      <div>
                        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Email Address</label>
                        <input type="email" placeholder="your@email.com" value={form.email}
                          onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                          style={{ width: '100%', padding: '12px 15px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none', transition: 'border 0.2s', background: '#fff', boxSizing: 'border-box' }}
                          onFocus={e => e.target.style.borderColor = '#005B66'}
                          onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                      </div>

                      <div>
                        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Interested Vertical</label>
                        <select value={form.vertical} onChange={e => setForm(p => ({ ...p, vertical: e.target.value }))}
                          style={{ width: '100%', padding: '12px 15px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14, fontFamily: 'Inter, sans-serif', background: '#fff', outline: 'none', boxSizing: 'border-box' }}>
                          <option value="">Select a vertical</option>
                          {['Med In Scans & Labs', 'Reach Multispeciality Clinics', 'Reach Home Medcare Services', 'Reach Pharma','Reach Scans and Labs', 'Reach Surgicals & Pharma', 'Reach Academy', 'Reach Impex', 'Reach Gardens', 'Reach Sports Academy'].map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </div>

                      <div>
                        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Message</label>
                        <textarea placeholder="Tell us how we can help you..." value={form.message}
                          onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={4}
                          style={{ width: '100%', height: isMobile ? 130 : 320, padding: '12px 15px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14, fontFamily: 'Inter, sans-serif', outline: 'none', resize: 'vertical', background: '#fff', boxSizing: 'border-box' }}
                          onFocus={e => e.target.style.borderColor = '#005B66'}
                          onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                      </div>

                      <MagneticButton>
                        <button onClick={handleSubmit} disabled={status === 'submitting'} className="btn-primary shine" style={{ width: '100%', justifyContent: 'center', padding: '15px', fontSize: 16, gap: 10, opacity: status === 'submitting' ? 0.7 : 1, cursor: status === 'submitting' ? 'not-allowed' : 'pointer' }}>
                          {status === 'submitting' ? (
                            <>Sending... <Loader2 size={16} className="spin-icon" /></>
                          ) : (
                            <>Send Message <Send size={16} /></>
                          )}
                        </button>
                      </MagneticButton>
                      <p style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>We typically respond within 2 hours during business hours.</p>
                    </div>
                  </>
                )}
              </div>

              {/* CONTACT INFO — dark teal half, split into two: contact details up
                  top, a dedicated "Our Locations" map card below */}
              <div className="matte-teal" style={{ padding: '44px 40px', position: 'relative', overflow: 'hidden', color: '#fff' }}>
                <div style={{ position: 'absolute', inset: 0, zIndex: 0, background: 'linear-gradient(150deg, #00464fcc 0%, #005B66aa 45%, #0B7078cc 100%)' }} />
                <div className="blob" style={{ top: -80, right: -80, width: 280, height: 280, background: 'radial-gradient(circle, rgba(214,40,40,0.22) 0%, transparent 70%)' }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <h2 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 22, color: '#fff', marginBottom: 6 }}>Contact Information</h2>
                  <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.65)', marginBottom: 26, maxWidth: 320, lineHeight: 1.6 }}>
                    Reach out any way that suits you we're a phone call, message or visit away.
                  </p>

                  <StaggerGroup style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 26 }}>
                    {infoItems.map((item, i) => (
                      <StaggerItem key={i}>
                        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, backdropFilter: 'blur(6px)' }}>
                            <item.Icon size={18} color="#fff" />
                          </div>
                          <div>
                            <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.title}</div>
                            {item.link ? (
                              <a href={item.link} style={{ fontSize: 14.5, color: '#fff', fontWeight: 500 }}>{item.info}</a>
                            ) : (
                              <div style={{ fontSize: 14.5, color: '#fff' }}>{item.info}</div>
                            )}
                          </div>
                        </div>
                      </StaggerItem>
                    ))}
                  </StaggerGroup>

                  <MagneticButton>
                    <a href={waUrl} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.3)', color: '#fff', padding: '11px 22px', borderRadius: 10, fontWeight: 700, fontSize: 14, fontFamily: 'Poppins, sans-serif', marginBottom: 28 }}>
                      <MessageCircle size={18} color="#25D366" /> Chat on WhatsApp
                    </a>
                  </MagneticButton>

                  <div>
                    <h4 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Follow Us</h4>
                    <div style={{ display: 'flex', gap: 10 }}>
                      {[{ Icon: FacebookIcon, label: 'Facebook' }].map(s => (
                        <a key={s.label} href="https://www.facebook.com/share/1J2ScrkhEN/" target="_blank" rel="noopener noreferrer" title={s.label}
                          style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', transition: 'all 0.2s' }}
                          onMouseEnter={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background = '#fff'; t.style.color = '#005B66'; }}
                          onMouseLeave={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background = 'rgba(255,255,255,0.1)'; t.style.color = '#fff'; }}>
                          <s.Icon size={17} />
                        </a>
                      ))}
                      {[{ Icon: InstagramIcon, label: 'Instagram' }].map(s => (
                        <a key={s.label} href="https://www.instagram.com/reachgroups26?igsi=b2czNTF2cnVwZW0" target="_blank" rel="noopener noreferrer" title={s.label}
                          style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', transition: 'all 0.2s' }}
                          onMouseEnter={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background = '#fff'; t.style.color = '#005B66'; }}
                          onMouseLeave={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background = 'rgba(255,255,255,0.1)'; t.style.color = '#fff'; }}>
                          <s.Icon size={17} />
                        </a>
                       ))}
                       {[{ Icon: TwitterIcon, label: 'Twitter' }].map(s => (
                        <a key={s.label} href="https://x.com/reachgroups" target="_blank" rel="noopener noreferrer" title={s.label}
                          style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', transition: 'all 0.2s' }}
                          onMouseEnter={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background = '#fff'; t.style.color = '#005B66'; }}
                          onMouseLeave={e => { const t = e.currentTarget as HTMLAnchorElement; t.style.background = 'rgba(255,255,255,0.1)'; t.style.color = '#fff'; }}>
                          <s.Icon size={17} />
                        </a>
                       ))}
                    </div>
                  </div>

                  {/* OUR LOCATIONS */}
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', marginTop: 28, paddingTop: 22 }}>
                    <h4 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Our Location</h4>
                    <a
                      href={tambaramSearchUrl} target="_blank" rel="noopener noreferrer" aria-label="Open Tambaram service area in Google Maps"
                      style={{ display: 'block', position: 'relative', height: 220, borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)' }}
                    >
                      <iframe
                        title="Reach Groups service area near Tambaram"
                        src={tambaramEmbedSrc}
                        width="100%" height="100%" style={{ border: 'none', display: 'block', pointerEvents: 'none' }}
                        loading="lazy" />
                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,20,22,0.02) 0%, rgba(0,20,22,0.3) 100%)', pointerEvents: 'none' }} />

                      <div style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(255,255,255,0.92)', color: '#005B66', fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <MapPin size={12} /> Open in Maps
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
        <style>{`
          @media(max-width:800px){ .contact-card{grid-template-columns:1fr!important} }
          .spin-icon { animation: spin 0.8s linear infinite; }
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}</style>
      </section>
    </>
  );
};

export default Contact;